import type {
  CanonicalCompatibilityEvidence,
} from '../tree/CanonicalTreeCompatibilityEdgeProjection';

import {
  createTreeRuntimeMissingParentCommitmentNonce,
  createTreeRuntimeMissingParentIdentityCommitment,
  deriveTreeRuntimeMissingParentReferencePairs,
  resolveTreeRuntimeMissingParentIdentityParity,
  type TreeRuntimeMissingParentIdentityParityDecision,
  type TreeRuntimeMissingParentRepairReadinessStatus,
} from './TreeRuntimeMissingParentIdentityParityAuthority';

export const TREE_RUNTIME_MISSING_PARENT_IDENTITY_BROWSER_MILESTONE =
  'CC-TREE-LIVE-003G2R' as const;

export const TREE_RUNTIME_MISSING_PARENT_IDENTITY_BROWSER_STATUSES =
  Object.freeze([
    'OWNER_INCOMPATIBLE',
    'INVALID_BROWSER_EVIDENCE',
    'CRYPTO_UNAVAILABLE',
    'RPC_FAILED',
    'DECIDED',
  ] as const);

export type TreeRuntimeMissingParentIdentityBrowserStatus =
  typeof TREE_RUNTIME_MISSING_PARENT_IDENTITY_BROWSER_STATUSES[number];

export interface TreeRuntimeMissingParentIdentityRpcEnvelope {
  readonly p_nonce: string;
  readonly p_commitment: string;
}

export interface TreeRuntimeMissingParentIdentityBrowserObservation {
  readonly milestone:
    typeof TREE_RUNTIME_MISSING_PARENT_IDENTITY_BROWSER_MILESTONE;

  readonly status:
    TreeRuntimeMissingParentIdentityBrowserStatus;

  readonly browserMissingReferencePairsCount:
    number | null;

  readonly identityParityProven:
    boolean;

  readonly repairPlanningEligible:
    boolean;

  readonly repairReadinessStatus:
    TreeRuntimeMissingParentRepairReadinessStatus | null;

  readonly decision:
    TreeRuntimeMissingParentIdentityParityDecision | null;

  readonly identitySuppressed: true;
  readonly readOnly: true;
  readonly repairExecuted: false;
  readonly repairReady: false;
}

const blockedObservation = (
  status:
    Exclude<
      TreeRuntimeMissingParentIdentityBrowserStatus,
      'DECIDED'
    >,
  browserMissingReferencePairsCount:
    number | null = null,
): TreeRuntimeMissingParentIdentityBrowserObservation =>
  Object.freeze({
    milestone:
      TREE_RUNTIME_MISSING_PARENT_IDENTITY_BROWSER_MILESTONE,
    status,
    browserMissingReferencePairsCount,
    identityParityProven: false,
    repairPlanningEligible: false,
    repairReadinessStatus: null,
    decision: null,
    identitySuppressed: true,
    readOnly: true,
    repairExecuted: false,
    repairReady: false,
  });

export const createTreeRuntimeMissingParentIdentityOwnerBlockedObservation =
  (): TreeRuntimeMissingParentIdentityBrowserObservation =>
    blockedObservation(
      'OWNER_INCOMPATIBLE',
    );

export async function runTreeRuntimeMissingParentIdentityBrowserRuntime(
  input: {
    readonly evidence:
      readonly CanonicalCompatibilityEvidence[];

    readonly invokeRpc:
      (
        request:
          TreeRuntimeMissingParentIdentityRpcEnvelope,
      ) => Promise<unknown>;

    readonly cryptoAvailable?: boolean;
  },
): Promise<TreeRuntimeMissingParentIdentityBrowserObservation> {
  const pairs =
    deriveTreeRuntimeMissingParentReferencePairs(
      input.evidence,
    );

  if (pairs === null) {
    return blockedObservation(
      'INVALID_BROWSER_EVIDENCE',
    );
  }

  const pairCount =
    pairs.length;

  if (input.cryptoAvailable === false) {
    return blockedObservation(
      'CRYPTO_UNAVAILABLE',
      pairCount,
    );
  }

  let nonce:
    string | null = null;

  try {
    nonce =
      createTreeRuntimeMissingParentCommitmentNonce();
  } catch {
    nonce =
      null;
  }

  if (nonce === null) {
    return blockedObservation(
      'CRYPTO_UNAVAILABLE',
      pairCount,
    );
  }

  let commitment:
    string | null = null;

  try {
    commitment =
      await createTreeRuntimeMissingParentIdentityCommitment({
        nonce,
        pairs,
      });
  } catch {
    commitment =
      null;
  }

  if (commitment === null) {
    return blockedObservation(
      'CRYPTO_UNAVAILABLE',
      pairCount,
    );
  }

  let response:
    unknown = null;

  try {
    response =
      await input.invokeRpc({
        p_nonce: nonce,
        p_commitment: commitment,
      });
  } catch {
    return blockedObservation(
      'RPC_FAILED',
      pairCount,
    );
  }

  const decision =
    resolveTreeRuntimeMissingParentIdentityParity({
      browserMissingReferencePairsCount:
        pairCount,
      response,
    });

  return Object.freeze({
    milestone:
      TREE_RUNTIME_MISSING_PARENT_IDENTITY_BROWSER_MILESTONE,
    status:
      'DECIDED',
    browserMissingReferencePairsCount:
      pairCount,
    identityParityProven:
      decision.identityParityProven,
    repairPlanningEligible:
      decision.repairPlanningEligible,
    repairReadinessStatus:
      decision.repairReadinessStatus,
    decision,
    identitySuppressed: true,
    readOnly: true,
    repairExecuted: false,
    repairReady: false,
  });
}
